import React, { useState } from 'react';

export interface TreeDocument {
  id: string;
  title: string;
  moduleId: string;
  isExternal: boolean;
}

export interface TreeModule {
  id: string;
  name: string;
  categoryId: string;
  documents: TreeDocument[];
}

export interface TreeCategory {
  id: string;
  name: string;
  sortOrder: number;
  modules: TreeModule[];
}

export type TreeAction =
  | { type: 'addCategory' }
  | { type: 'addModule'; categoryId: string }
  | { type: 'addDocument'; moduleId: string }
  | { type: 'renameCategory'; categoryId: string; currentName: string }
  | { type: 'renameModule'; moduleId: string; currentName: string }
  | { type: 'editDocument'; documentId: string }
  | { type: 'deleteCategory'; categoryId: string; hasChildren: boolean }
  | { type: 'deleteModule'; moduleId: string; hasChildren: boolean }
  | { type: 'deleteDocument'; documentId: string };

interface DocumentTreeProps {
  treeData: TreeCategory[];
  selectedId: string | null;
  onSelect: (docId: string) => void;
  onAction: (action: TreeAction) => void;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    fontSize: '0.85rem',
    userSelect: 'none',
  },
  addCategoryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.4rem 0.6rem',
    marginBottom: '0.5rem',
    background: 'none',
    border: '1px dashed #ccc',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.8rem',
    color: '#1a6b3c',
    width: '100%',
  },
  categoryNode: {
    marginBottom: '0.25rem',
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.35rem 0.5rem',
    cursor: 'pointer',
    borderRadius: '4px',
    fontWeight: 600,
    color: '#0f2d1f',
    gap: '0.3rem',
  },
  moduleNode: {
    marginLeft: '1rem',
    marginBottom: '0.15rem',
  },
  moduleHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.3rem 0.5rem',
    cursor: 'pointer',
    borderRadius: '4px',
    fontWeight: 500,
    color: '#333',
    gap: '0.3rem',
  },
  documentNode: {
    marginLeft: '2rem',
    marginBottom: '0.1rem',
  },
  documentItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.25rem 0.5rem',
    cursor: 'pointer',
    borderRadius: '4px',
    color: '#444',
    gap: '0.3rem',
  },
  documentItemSelected: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.25rem 0.5rem',
    cursor: 'pointer',
    borderRadius: '4px',
    color: '#1a6b3c',
    gap: '0.3rem',
    backgroundColor: '#e8f5e9',
    fontWeight: 600,
  },
  actions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: '0.2rem',
    opacity: 0,
    transition: 'opacity 0.15s',
  },
  actionsVisible: {
    marginLeft: 'auto',
    display: 'flex',
    gap: '0.2rem',
    opacity: 1,
  },
  actionBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: '0.1rem 0.2rem',
    borderRadius: '3px',
    lineHeight: 1,
  },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
};

export function DocumentTree({ treeData, selectedId, onSelect, onAction }: DocumentTreeProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div style={styles.container}>
      <button
        style={styles.addCategoryBtn}
        onClick={() => onAction({ type: 'addCategory' })}
      >
        ➕ Add Category
      </button>

      {treeData.map((category) => (
        <div key={category.id} style={styles.categoryNode}>
          <div
            style={styles.categoryHeader}
            onClick={() => toggleExpand(category.id)}
            onMouseEnter={() => setHoveredNode(`cat-${category.id}`)}
            onMouseLeave={() => setHoveredNode(null)}
          >
            <span>{expanded[category.id] ? '📂' : '📁'}</span>
            <span style={styles.label}>{category.name}</span>
            <span style={hoveredNode === `cat-${category.id}` ? styles.actionsVisible : styles.actions}>
              <button
                style={styles.actionBtn}
                title="Add module"
                onClick={(e) => { e.stopPropagation(); onAction({ type: 'addModule', categoryId: category.id }); }}
              >➕</button>
              <button
                style={styles.actionBtn}
                title="Rename"
                onClick={(e) => { e.stopPropagation(); onAction({ type: 'renameCategory', categoryId: category.id, currentName: category.name }); }}
              >✏️</button>
              <button
                style={styles.actionBtn}
                title="Delete"
                onClick={(e) => { e.stopPropagation(); onAction({ type: 'deleteCategory', categoryId: category.id, hasChildren: category.modules.length > 0 }); }}
              >🗑️</button>
            </span>
          </div>

          {expanded[category.id] && category.modules.map((mod) => (
            <div key={mod.id} style={styles.moduleNode}>
              <div
                style={styles.moduleHeader}
                onClick={() => toggleExpand(mod.id)}
                onMouseEnter={() => setHoveredNode(`mod-${mod.id}`)}
                onMouseLeave={() => setHoveredNode(null)}
              >
                <span>{expanded[mod.id] ? '📂' : '📁'}</span>
                <span style={styles.label}>{mod.name}</span>
                <span style={hoveredNode === `mod-${mod.id}` ? styles.actionsVisible : styles.actions}>
                  <button
                    style={styles.actionBtn}
                    title="Add document"
                    onClick={(e) => { e.stopPropagation(); onAction({ type: 'addDocument', moduleId: mod.id }); }}
                  >➕</button>
                  <button
                    style={styles.actionBtn}
                    title="Rename"
                    onClick={(e) => { e.stopPropagation(); onAction({ type: 'renameModule', moduleId: mod.id, currentName: mod.name }); }}
                  >✏️</button>
                  <button
                    style={styles.actionBtn}
                    title="Delete"
                    onClick={(e) => { e.stopPropagation(); onAction({ type: 'deleteModule', moduleId: mod.id, hasChildren: mod.documents.length > 0 }); }}
                  >🗑️</button>
                </span>
              </div>

              {expanded[mod.id] && mod.documents.map((doc) => (
                <div key={doc.id} style={styles.documentNode}>
                  <div
                    style={selectedId === doc.id ? styles.documentItemSelected : styles.documentItem}
                    onClick={() => onSelect(doc.id)}
                    onMouseEnter={() => setHoveredNode(`doc-${doc.id}`)}
                    onMouseLeave={() => setHoveredNode(null)}
                  >
                    <span>{doc.isExternal ? '🔗' : '📄'}</span>
                    <span style={styles.label}>{doc.title}</span>
                    <span style={hoveredNode === `doc-${doc.id}` ? styles.actionsVisible : styles.actions}>
                      <button
                        style={styles.actionBtn}
                        title="Edit"
                        onClick={(e) => { e.stopPropagation(); onAction({ type: 'editDocument', documentId: doc.id }); }}
                      >✏️</button>
                      <button
                        style={styles.actionBtn}
                        title="Delete"
                        onClick={(e) => { e.stopPropagation(); onAction({ type: 'deleteDocument', documentId: doc.id }); }}
                      >🗑️</button>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
